import Label from "@components/Label";
import { LockedButton } from "@components/lockedButtons/LockedButton";
import usePermissions from "@hooks/usePermissions";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DocumentSeriesDialog } from "@pages/ManualDocumentV2/components/ManualDocumentSeries/DocumentSeriesDialog";
import { manualDocNumberStyles } from "@utils/cssStyles";
import { Info, Pencil } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import type { IFormConfig } from "../core/types";

interface IDocumentHeaderProps {
  config: IFormConfig;
  translate: (text: any, options?: any) => string;
  onSeriesUpdated: () => void;
  hideEmptyDocumentNumber?: boolean;
  isEditPOPageVisible?: boolean;
}

export const DocumentHeader = ({
  config,
  translate,
  onSeriesUpdated,
  hideEmptyDocumentNumber = false,
  isEditPOPageVisible = false,
}: IDocumentHeaderProps) => {
  const { getValues } = useFormContext<any>();
  const { documentNumber } = getValues();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { permissionHandle } = usePermissions();

  const seriesPermissionLocked = config.seriesPermission
    ? !permissionHandle([config.seriesPermission])
    : true;

  const titleText = config.complianceStandardKey
    ? translate("create_compliance_standard_document", {
        documentType: translate(config.titleKey ?? config.id),
        complianceStandard: translate(config.complianceStandardKey),
      })
    : translate(config.titleKey ?? config.id);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mt: 1,
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h3"
          sx={{
            wordBreak: "break-word",
          }}
        >
          {titleText}
        </Typography>
        {isEditPOPageVisible && (
          <Stack direction="row" spacing={0.5}>
            <Info size={16} strokeWidth={1.5} />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", wordBreak: "break-word" }}
            >
              {translate("created_in_dost_portal_edit_message")}
            </Typography>
          </Stack>
        )}
      </Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: "center", width: { xs: "100%", md: "auto" } }}
      >
        {(documentNumber || !hideEmptyDocumentNumber) && (
          <Label
            variant="outlined"
            color="primary"
            sx={{ py: 2.5, px: 2, borderRadius: "28px" }}
          >
            <Tooltip
              title={documentNumber || translate("no_invoice_number")}
              placement="top"
              arrow
            >
              <Typography variant="body1" noWrap sx={manualDocNumberStyles}>
                {documentNumber
                  ? documentNumber
                  : translate("no_invoice_number")}
              </Typography>
            </Tooltip>
          </Label>
        )}

        {config.complianceType !== undefined && (
          <>
            <LockedButton
              locked={seriesPermissionLocked}
              variant="outlined"
              endIcon={<Pencil strokeWidth="1.5px" size="18px" />}
              onClick={() => setIsDialogOpen(true)}
              buttonText={
                <Tooltip
                  title={translate("customize_invoice_sequencing")}
                  placement="top"
                >
                  <Typography
                    noWrap
                    sx={{
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                      cursor: "pointer",
                    }}
                  >
                    {translate("customize_invoice_sequencing")}
                  </Typography>
                </Tooltip>
              }
              sx={{ px: "8px" }}
            />
            <DocumentSeriesDialog
              isOpen={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              onRefreshParent={onSeriesUpdated}
              documentClass={config.documentClass as any}
              complianceType={config.complianceType as any}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};
